import type { CSSProperties, ReactElement, ReactNode } from "react";
import { layout, space } from "@tokens/index";
import { SkeletonBlock } from "@primitives/Skeleton";
import { Text } from "@primitives/Text";
import { errorBlock, listRow, listStack, rowMain, type ListRowMaterial } from "./listSurface";

/**
 * The three non-content states every list surface shares.
 *
 *  - `ListSkeleton`: `SkeletonBlock`s laid out on the list row grid, so the
 *    placeholder keeps the geometry of the rows it replaces. `role="status"`.
 *  - `ListEmptyLine`: one line of plain text that names what is absent.
 *  - `ListError`: `role="alert"`, danger tone, the message.
 */

export interface ListSkeletonProps {
  /** Accessible label, for example "Loading queues". */
  readonly label: string;
  readonly count?: number;
  readonly material?: ListRowMaterial;
  /** Width of the leading glyph slot in px. `0` omits it. */
  readonly leading?: number;
  /** Render a second, shorter line under the title line. */
  readonly twoLine?: boolean;
  /** Width of the trailing block in px. `0` omits it. */
  readonly trailing?: number;
}

const idle = { hovered: false, selected: false } as const;

export function ListSkeleton({
  label,
  count = 3,
  material = "transparent",
  leading = 16,
  twoLine = false,
  trailing = 32,
}: ListSkeletonProps): ReactElement {
  const row: CSSProperties = { ...listRow(material, idle), alignItems: "center" };
  return (
    <div role="status" aria-label={label} aria-live="polite" aria-busy="true" style={listStack}>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} style={row} aria-hidden="true">
          {leading > 0 && <SkeletonBlock width={leading} height={leading} />}
          <div style={rowMain}>
            <SkeletonBlock width="60%" height={14} />
            {twoLine && <SkeletonBlock width="40%" height={12} />}
          </div>
          {trailing > 0 && <SkeletonBlock width={trailing} height={12} />}
        </div>
      ))}
    </div>
  );
}

export interface ListEmptyLineProps {
  readonly children: ReactNode;
}

export function ListEmptyLine({ children }: ListEmptyLineProps): ReactElement {
  return (
    <Text
      as="p"
      role="bodySm"
      tone="muted"
      style={{ padding: `${space[3]} ${space[3]}`, maxWidth: layout.proseMaxWidth }}
    >
      {children}
    </Text>
  );
}

export interface ListErrorProps {
  readonly children: ReactNode;
}

export function ListError({ children }: ListErrorProps): ReactElement {
  return (
    <div role="alert" style={errorBlock}>
      <Text role="bodySm">{children}</Text>
    </div>
  );
}
